/**
 * 按钮权限同步 Hook
 * 用于检测按钮权限与接口权限的一致性，并提供同步功能
 */
import { useCallback, useContext, useMemo, useState } from 'react';

import { ConfigContext } from '@/components/ConfigProvider';
import { matchCasbinPolicy, useDom } from '@/hooks/useAuth';
import { getAuthRelyOn } from '@/utils/getAuthRelyOn';

import type { PermissionSyncConfig, PermissionSyncResult, PolicyItem } from './types';
import { parsePermissionKey, permissionKeyToPolicy } from './utils';

/**
 * 与 useAuthFun / 菜单 enforceEx 一致解析请求作用域。
 * 租户侧固定 datacenter='-'（即使 URL 带 cluster）；策略里的 '-' 是字面空域，不是通配。
 */
function resolveRequestScope(domInfo: { module?: string; cluster?: string; workspace?: string }) {
  if (domInfo.module === 'tenant') {
    return { datacenter: '-', workspace: domInfo.workspace || '-' };
  }
  if (domInfo.module === 'data-center') {
    return { datacenter: domInfo.cluster || '-', workspace: '-' };
  }
  return { datacenter: '-', workspace: '-' };
}

/**
 * 权限同步 Hook
 * @param config 配置项
 * @returns 权限同步结果
 */
export function usePermissionSync(config: PermissionSyncConfig): PermissionSyncResult {
  const { permission, hasPermission, requiredApis: requiredApisInput, authMap, syncFn, onSyncSuccess, onSyncError } = config;
  const { policies } = useContext(ConfigContext);

  const [syncing, setSyncing] = useState(false);

  // 稳定 requiredApis 的引用，避免每次渲染创建新对象
  const requiredApis = useMemo(
    () => (authMap && permission ? getAuthRelyOn(authMap, permission) : requiredApisInput || {}),
    [requiredApisInput, authMap, permission],
  );
  // 序列化 requiredApis 用于依赖比较
  const requiredApisKey = useMemo(() => JSON.stringify(Object.keys(requiredApis).sort()), [requiredApis]);

  // 获取当前环境信息（useDom 本身是 Hook，必须直接调用，不能放在 useMemo 内）
  const dom = useDom();
  const domInfo = useMemo(() => dom, [dom]);

  // 检测按钮权限（与菜单 enforceEx 同一套 matchCasbinPolicy，避免跨作用域误授权）
  const _hasPermission = useMemo(() => {
    // 外部传入了权限，直接返回
    if (hasPermission !== undefined) return hasPermission;

    // 未传 permission（如广场公开访问）时视为无权限，避免读 undefined.type
    if (!permission) return false;

    if (!policies || policies.length === 0) return false;

    const { datacenter, workspace } = resolveRequestScope(domInfo);

    for (const policy of policies) {
      if (matchCasbinPolicy(policy, '*', datacenter, workspace, permission.group, '*', permission.resource, permission.type)) {
        return true;
      }
    }

    return false;
  }, [policies, permission, hasPermission, domInfo]);

  // 检测缺失的接口权限
  const missingApis = useMemo(() => {
    if (!_hasPermission || Object.keys(requiredApis).length === 0) {
      return [];
    }

    const missing: string[] = [];
    const { datacenter, workspace } = resolveRequestScope(domInfo);

    Object.keys(requiredApis).forEach((key) => {
      const parsed = parsePermissionKey(key);
      if (!parsed) return;

      const hasApiPermission = (policies || []).some((policy) =>
        matchCasbinPolicy(policy, '*', datacenter, workspace, parsed.group, '*', parsed.resource, parsed.type),
      );

      if (!hasApiPermission) {
        missing.push(key);
      }
    });

    return missing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_hasPermission, requiredApisKey, policies, domInfo]);

  // 是否需要同步
  const needSync = useMemo(() => {
    return _hasPermission && missingApis.length > 0;
  }, [_hasPermission, missingApis]);

  // 同步权限
  const syncPermission = useCallback(async () => {
    if (!needSync || syncing) return;

    setSyncing(true);
    try {
      // 构建缺失的权限策略
      const newPolicies: PolicyItem[] = missingApis
        .map((key) => permissionKeyToPolicy(key, domInfo))
        .filter((p): p is PolicyItem => p !== null);

      if (newPolicies.length === 0) {
        console.warn('没有需要同步的权限');
        return;
      }

      if (syncFn) {
        // 使用自定义同步函数执行真实的接口调用
        await syncFn(missingApis, policies);
      } else {
        // 占位：未提供 syncFn 时打印待同步策略，业务方需传入 syncFn 实现真实同步
        console.log('需要同步的权限:', newPolicies);
      }

      onSyncSuccess?.();
    } catch (error) {
      console.error('同步权限失败:', error);
      onSyncError?.(error as Error);
    } finally {
      setSyncing(false);
    }
  }, [needSync, syncing, missingApis, domInfo, syncFn, policies, onSyncSuccess, onSyncError]);

  return {
    hasPermission: _hasPermission,
    needSync,
    missingApis,
    syncing,
    syncPermission,
  };
}
